import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { catchError, map, of } from 'rxjs';
import {
  Users,
  ShieldCheck,
  Heart,
  MapPin,
  Clock,
  LucideIconData,
} from 'lucide-angular';

import { AosDirective } from '../../shared/directives/aos.directive';
import { CountUpDirective } from '../../shared/directives/count-up.directive';
import { IconComponent } from '../../shared/ui';
import { ComunidadBlockComponent } from '../home/blocks/comunidad-block/comunidad-block.component';
import { HomeBlocksService } from '../home/data/home-blocks.service';
import { PublicCommunityData, PublicHomeBlock } from '../home/data/home-blocks.model';
import { MOCK_ABOUT } from './data/mock-about';

interface CommunityState {
  loading: boolean;
  data: PublicCommunityData | null;
}

@Component({
  selector: 'app-about-page',
  imports: [IconComponent, ComunidadBlockComponent, AosDirective, CountUpDirective],
  templateUrl: './about-page.component.html',
  styleUrl: './about-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class AboutPageComponent {
  protected readonly data = MOCK_ABOUT;

  private readonly sanitizer = inject(DomSanitizer);
  private readonly homeBlocksService = inject(HomeBlocksService);

  private readonly communityState = toSignal(
    this.homeBlocksService.getHome().pipe(
      map((blocks): CommunityState => {
        const community = blocks.find(
          (block): block is Extract<PublicHomeBlock, { type: 'community' }> =>
            block.type === 'community',
        );
        return { loading: false, data: community?.data ?? null };
      }),
      catchError(() => of<CommunityState>({ loading: false, data: null })),
    ),
    { initialValue: { loading: true, data: null } as CommunityState },
  );

  protected readonly communityLoading = computed(() => this.communityState().loading);
  protected readonly communityData = computed(() => this.communityState().data);

  protected readonly mapPinIcon: LucideIconData = MapPin;
  protected readonly clockIcon: LucideIconData = Clock;

  protected readonly mapEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    'https://www.google.com/maps?q=Plaza+Del+Castillo+Zapote+San+Jose+Costa+Rica&output=embed'
  );

  protected readonly valueIcons: Record<string, LucideIconData> = {
    users: Users,
    'shield-check': ShieldCheck,
    heart: Heart,
  };
}
